import { Icon } from "@2u-landing-pages/components/Icon";
import EventClient from "@2u-landing-pages/utils/event-client";
import { Button as ButtonV1, Input } from "@red-digital/bricks";
import { Button } from "@red-digital/bricks/dist/components/Button/ButtonV2";
import cn from "classnames";
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FilterCfg } from "../../FilterBar.types";
import { filterKeyMap } from "../../filterAndSort";

// @ts-ignore
import styles from "../../FilterBar.module.scss";

type PortalProps = {
  children: React.ReactNode;
  condition: boolean;
  target?: HTMLElement;
};

const ConditionalPortal: React.FC<PortalProps> = ({ children, condition, target }) => {
  if (condition && typeof document !== "undefined" && target) {
    return ReactDOM.createPortal(children, target);
  }
  return <>{children}</>;
};

export type PopupFilterProps = {
  filterOpened: boolean;
  isClosing: boolean;
  bottomOverlay: boolean;
  onToggle: (open: boolean) => void;
  onResetFilters: () => void;
  useButtonsFilter: boolean;
  btnsFilterGroups: FilterCfg;
  filterGroupOptions: FilterCfg;
  filterLabels: Record<string, string>;
  showOptionTotals: boolean;
  filters: Record<string, string[] | null>;
  onFilterChange: (key: string, value: string, checked: boolean) => void;
};

export const PopupFilter: React.FC<PopupFilterProps> = ({
  filterOpened,
  isClosing,
  bottomOverlay,
  onToggle,
  onResetFilters,
  useButtonsFilter,
  btnsFilterGroups,
  filterGroupOptions,
  filterLabels,
  showOptionTotals,
  filters,
  onFilterChange,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onToggle(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onToggle]);

  return (
    <>
      <Button
        ref={buttonRef}
        className={styles.galleryFilterButton}
        variant="filled"
        color="light"
        icon={{
          component: <Icon name="filter" />,
          position: "left",
        }}
        onClick={() => {
          onToggle(!filterOpened);

          EventClient.elementClicked({
            webElement: {
              elementType: "Landing Page",
              htmlId: "",
              location: "Gallery",
              name: "Filters",
              position: "Gallery",
              text: "Filters",
            },
          });
        }}
      >
        Filters
      </Button>
      <ConditionalPortal
        condition={bottomOverlay}
        target={typeof document !== "undefined" ? document.body : undefined}
      >
        <div
          ref={panelRef}
          className={cn(styles.galleryFilterPanel, {
            [styles.galleryFilterPanelOpen]: filterOpened,
            [styles.galleryFilterPanelPortal]: bottomOverlay,
            [styles.galleryFilterPanelClosing]: isClosing,
          })}
        >
          <div className={styles.galleryFilterHeader}>
            <h3>Filters</h3>
            <div>
              <ButtonV1 className={styles.galleryFiltersReset} onClick={onResetFilters} size="medium">
                Reset filters
              </ButtonV1>
              <ButtonV1
                className={styles.galleryFilterClose}
                icon={{
                  component: <Icon name="x" size="lg" />,
                }}
                onClick={() => {
                  onToggle(false);

                  EventClient.elementClicked({
                    webElement: {
                      elementType: "Landing Page",
                      htmlId: "",
                      location: "Popup Filter",
                      name: "Close button",
                      position: "Popup Filter",
                      text: "Close button",
                    },
                  });
                }}
              />
            </div>
          </div>
          <div className={styles.galleryFilterGroupsContainer}>
            {Object.keys(useButtonsFilter ? btnsFilterGroups : filterGroupOptions).map(
              (group) =>
                (useButtonsFilter ? btnsFilterGroups : filterGroupOptions)[group].length > 0 && (
                  <div key={group} className={styles.galleryFilterGroup}>
                    <span>{filterLabels?.[filterKeyMap[group as keyof typeof filterKeyMap]] || group}</span>
                    {(useButtonsFilter ? btnsFilterGroups : filterGroupOptions)[group].map((option: any) => (
                      <Input
                        key={option.value}
                        type="checkbox"
                        label={{
                          children: showOptionTotals ? `${option.name} (${option.total})` : option.name,
                        }}
                        checked={
                          filters?.[filterKeyMap[group as keyof typeof filterKeyMap]]?.includes(option.value) || false
                        }
                        onChange={(e) => {
                          onFilterChange(
                            filterKeyMap[group as keyof typeof filterKeyMap],
                            option.value,
                            e.target.checked
                          );

                          EventClient.elementClicked({
                            webElement: {
                              elementType: group,
                              htmlId: e.target.checked ? "add" : "remove",
                              location: "Popup Filter",
                              name: option.value,
                              position: group,
                              text: option.value,
                            },
                          });
                        }}
                      />
                    ))}
                  </div>
                )
            )}
          </div>
        </div>
      </ConditionalPortal>
    </>
  );
};
